import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import {
  QBox,
  QButton,
  QFormControl,
  QSelect,
  QSelectPlaceholder,
  QText,
  useToastProvider,
} from '@qualio/ui-components';

import { documentApi } from '../../../api/document';
import { QualioDocumentAttachment } from '../../../api/model/document';
import { fileTypeFilter } from '../../../util/FileUploadUtils';

import {
  DOCUMENT_CONVERSION_CHANGE_FORMAT_ERROR,
  logCustomError,
} from '../../../messages/LogErrorMessages';
import { sortByLabel } from '../../../util/SelectionOptionUtils';
import { BaseDocumentConvertModal } from './BaseDocumentConvertModal';
import { DocumentConvertMessageConfig } from './DocumentConvertMessageConfig';
import {
  ConvertingStatus,
  ConvertionType,
  DocumentConvertFormatModalProps,
} from './types';

export const ConvertFileFormat = ({
  onClose,
  qualioDocument,
  onSuccess,
  onError,
}: DocumentConvertFormatModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();

  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number>();
  const [convertingStatus, setConvertingStatus] = useState<ConvertingStatus>({
    converting: false,
  });

  const { company_id: companyId, id: documentId } = qualioDocument;
  const getAttachmentsOptions = async () => {
    const doc = await documentApi.fetch(documentId.toString());
    return doc.sections
      .flatMap((section) => section.section_attachments ?? [])
      .filter((attachment) => fileTypeFilter(attachment))
      .map((attachment: QualioDocumentAttachment) => ({
        label: attachment.filename,
        value:
          typeof attachment.id === 'string'
            ? Number.parseInt(attachment.id)
            : attachment.id,
      }))
      .sort(sortByLabel);
  };

  const {
    error,
    isLoading,
    data: attachmentOptions,
  } = useQuery({
    queryFn: getAttachmentsOptions,
    queryKey: ['attachmentOptions'],
  });

  if (error) {
    showToast({
      'data-cy': 'convert-file-format-notify-error-toast',
      title: t('notifications.error'),
      description: t('convertFormat.errorLoadingAttachments'),
      status: 'error',
    });
  }

  const handleDocumentConversion = (attachmentId?: number) => {
    setConvertingStatus({
      converting: true,
      type: attachmentId
        ? ConvertionType.WithAttachment
        : ConvertionType.WithoutAttachment,
    });
    documentApi
      .convertToFileDocument(companyId, documentId, attachmentId)
      .then(() => {
        if (onSuccess) {
          onSuccess();
        }
        onClose(true);
      })
      .catch((error) => {
        logCustomError(DOCUMENT_CONVERSION_CHANGE_FORMAT_ERROR, {
          error,
          companyId: companyId,
          documentId: documentId,
          entityId: attachmentId,
        });

        showToast({
          'data-cy': 'convert-file-format-notify-error-toast',
          title: t('notifications.error'),
          description: t('convertFormat.couldNotChangeDocumentFormat'),
          status: 'error',
        });

        if (onError) {
          onError();
        }

        onClose(false);
      });
  };

  const attachmentDropdown = (
    <QFormControl label={t('labels.attachmentToConvert')}>
      <QBox data-cy="convert-file-format-attachment-select">
        <QSelect<string, number>
          isLoading={isLoading}
          options={attachmentOptions || []}
          value={selectedAttachmentId}
          onChange={(selected) => setSelectedAttachmentId(selected?.value)}
        >
          <QSelectPlaceholder>
            <QText>{t('forms.selectAFile')}</QText>
          </QSelectPlaceholder>
        </QSelect>
      </QBox>
    </QFormControl>
  );

  const modalActions = [
    <QButton
      key="convert-file-format-cancel-button"
      data-cy="convert-file-format-cancel-button"
      variant="ghost"
      onClick={() => onClose(false)}
    >
      {t('buttons.cancel')}
    </QButton>,
    <QButton
      key="convert-file-format-upload-new-button"
      data-cy="convert-file-format-upload-new-button"
      variant={'outline'}
      onClick={() => handleDocumentConversion()}
      isLoading={
        convertingStatus.converting &&
        convertingStatus.type === ConvertionType.WithoutAttachment
      }
      isDisabled={convertingStatus.converting}
    >
      {t('buttons.uploadNew')}
    </QButton>,
    <QButton
      key="convert-file-format-confirm-button"
      data-cy="convert-file-format-confirm-button"
      isLoading={
        convertingStatus.converting &&
        convertingStatus.type === ConvertionType.WithAttachment
      }
      isDisabled={!selectedAttachmentId || convertingStatus.converting}
      onClick={() => handleDocumentConversion(selectedAttachmentId)}
    >
      {t('buttons.confirm')}
    </QButton>,
  ];

  return (
    <BaseDocumentConvertModal
      {...DocumentConvertMessageConfig.QualioNativeDocument}
      extendedModalBodyFragment={attachmentDropdown}
      modalActionsFragment={modalActions}
      onClose={onClose}
    />
  );
};
